import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { passkeysSupported, loginWithPasskey } from "@/lib/webauthn";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_desktop-converter-9/artifacts/lfkzv9nu_IMG_0020.PNG";

export default function Login() {
  const { login, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email.trim(), pw);
      toast.success("Bienvenido/a");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Error al iniciar sesión";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const doPasskey = async () => {
    if (!passkeysSupported()) {
      toast.error("Este navegador no soporta passkeys");
      return;
    }
    setPasskeyBusy(true);
    try {
      await loginWithPasskey();
      // AuthContext picks up the new token on next me() call — force it
      if (refresh) await refresh();
      else window.location.reload();
      toast.success("Bienvenido/a");
    } catch (e) {
      if (e?.name === "NotAllowedError") {
        toast.error("Login cancelado");
      } else {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : (e.message || "Error al entrar con passkey"));
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: "radial-gradient(ellipse at top,#1a2942 0%,#0E1726 60%)" }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} className="w-20 h-20 rounded-2xl bg-white p-2 object-contain shadow-2xl" alt="Convitel"/>
          <div className="mt-4 text-white font-extrabold text-2xl tracking-tight">CONVITEL</div>
          <div className="text-white/60 text-xs font-medium tracking-widest">COMUNICACIONES · ERP</div>
        </div>
        <div className="card p-8">
          <h1 className="text-xl font-bold mb-1">Accede a tu cuenta</h1>
          <p className="text-sm text-slate-500 mb-6">Gestiona clientes, proyectos, personal y ventas.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" required autoComplete="username"
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="tu@correo.com" data-testid="login-email"/>
            </div>
            <div>
              <label className="label">Contraseña</label>
              <input className="input" type="password" required autoComplete="current-password"
                     value={pw} onChange={(e) => setPw(e.target.value)}
                     placeholder="••••••••" data-testid="login-password"/>
            </div>
            <button type="submit" disabled={busy}
                    className="btn btn-primary w-full justify-center !py-3"
                    data-testid="login-submit">
              {busy ? "Entrando..." : "Entrar"}
            </button>
          </form>

          {passkeysSupported() && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200"/>
                <span className="text-[11px] text-slate-400 font-medium uppercase tracking-widest">o</span>
                <div className="flex-1 h-px bg-slate-200"/>
              </div>
              <button
                type="button"
                onClick={doPasskey}
                disabled={passkeyBusy}
                className="btn btn-ghost w-full justify-center !py-3"
                data-testid="login-passkey"
              >
                <svg className="w-5 h-5 text-[var(--cvt-orange)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0-3 2-5 5-5s5 2 5 5v1M2 11c0-3 2-5 5-5 1.5 0 2.8.6 3.7 1.6M7 21v-6M14 21v-4c0-3-2-5-5-5s-5 2-5 5v4M17 17v4"/>
                </svg>
                {passkeyBusy ? "Comprobando..." : "Entrar con huella / Face ID"}
              </button>
            </>
          )}
          <div className="text-[11px] text-slate-400 mt-6 text-center">
            Acceso restringido · CONVITEL Comunicaciones
          </div>
        </div>
      </div>
    </div>
  );
}
